import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Download,
  FileText,
  Mail,
  Share2,
  Star,
  TrendingUp,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'

type ReportChild = {
  id: string
  name: string
  grade: string
  code: string
}

type LocationState = {
  child?: ReportChild
}

const DEFAULT_CHILD: ReportChild = {
  id: '1',
  name: 'Sarah Johnson',
  grade: 'Grade 5th',
  code: 'CAABCD12',
}

const SUMMARY_CARDS = [
  {
    label: 'Total hours',
    value: '127.5',
    helper: '+12% vs last month',
    helperTone: 'positive' as const,
  },
  {
    label: 'Average score',
    value: '91%',
    helper: 'Strong improvement',
    helperTone: 'neutral' as const,
  },
  {
    label: 'Completed',
    value: '47 / 65',
    helper: 'Lessons finished',
    helperTone: 'neutral' as const,
  },
  {
    label: 'Assignments completed',
    value: '95%',
    helper: 'Excellent',
    helperTone: 'positive' as const,
  },
] as const

const RECENT_ACTIVITY = [
  {
    id: '1',
    title: 'Completed Lesson 14: Loops and Functions',
    meta: 'Today · Python for Beginners',
    tone: 'success' as const,
  },
  {
    id: '2',
    title: 'Submitted Assignment: Number Guessing Game',
    meta: 'Yesterday · Graded · 95%',
    tone: 'info' as const,
  },
  {
    id: '3',
    title: 'Earned Gold Level Badge',
    meta: 'This week · Rewards',
    tone: 'reward' as const,
  },
  {
    id: '4',
    title: 'Achieved 100% on Quiz 5',
    meta: 'Last week · Conditionals',
    tone: 'success' as const,
  },
] as const

export const ParentReports: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const state = (location.state ?? {}) as LocationState
  const child = state.child ?? DEFAULT_CHILD

  const initials = child.name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.PARENT.DASHBOARD)}
        className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to dashboard
      </button>

      <div className="rounded-3xl border border-gray-100 bg-white px-6 py-5 shadow-sm sm:px-7 sm:py-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-800 shadow-sm">
              {initials}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {child.name}
              </h1>
              <p className="mt-0.5 text-xs text-gray-500">
                Detailed progress overview · {child.grade}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <span className="text-[11px] text-gray-500">Current module</span>
              <span className="text-xs font-semibold text-gray-900">
                Python for Beginners
              </span>
            </button>
            <Button
              type="button"
              variant="ghost"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              icon={<Share2 className="h-3.5 w-3.5" />}
            >
              Share
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              icon={<Mail className="h-3.5 w-3.5" />}
            >
              Email
            </Button>
            <Button
              type="button"
              variant="primary"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F28C28] px-3.5 py-1.5 text-xs font-semibold shadow-[0_12px_30px_rgba(242,140,40,0.45)] hover:bg-amber-600 focus:ring-amber-500"
              icon={<Download className="h-3.5 w-3.5" />}
            >
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {SUMMARY_CARDS.map(card => (
          <div
            key={card.label}
            className={cn(
              'flex flex-col justify-between rounded-2xl border-2 p-5 shadow-sm bg-white',
              card.helperTone === 'positive' && 'border-[#D0EAD9] bg-[#F4F8F7]',
              card.helperTone === 'neutral' && 'border-[#E5E7EB] bg-[#F9FAFB]'
            )}
          >
            <p className="text-xs font-medium text-gray-500">{card.label}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-gray-900">
                {card.value}
              </span>
            </div>
            <p
              className={cn(
                'mt-1 text-[11px]',
                card.helperTone === 'positive'
                  ? 'text-emerald-600'
                  : 'text-[#717392]'
              )}
            >
              {card.helper}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-7">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Recent activity
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              A snapshot of {child.name.split(' ')[0]}&apos;s latest learning
              milestones.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            icon={<TrendingUp className="h-3.5 w-3.5" />}
          >
            View full history
          </Button>
        </div>

        <div className="mt-5 space-y-3">
          {RECENT_ACTIVITY.map((activity, index) => (
            <div
              key={activity.id}
              className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-3"
            >
              <div className="mt-0.5 flex flex-col items-center">
                <span
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full text-white',
                    activity.tone === 'success' && 'bg-[#34A853]',
                    activity.tone === 'info' && 'bg-[#1D5AA6]',
                    activity.tone === 'reward' && 'bg-[#F28C28]'
                  )}
                >
                  {activity.tone === 'success' && (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {activity.tone === 'info' && (
                    <Star className="h-3.5 w-3.5" />
                  )}
                  {activity.tone === 'reward' && (
                    <Star className="h-3.5 w-3.5" />
                  )}
                </span>
                {index < RECENT_ACTIVITY.length - 1 && (
                  <span className="mt-1 h-8 w-px bg-gray-200" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {activity.title}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {activity.meta}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p className="text-[11px] text-gray-500">
            Report generated automatically based on recent classes,
            assignments, and quizzes.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              icon={<FileText className="h-3.5 w-3.5" />}
            >
              View certificates
            </Button>
            <Button
              type="button"
              variant="primary"
              className="rounded-lg bg-[#1D5AA6] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-800 focus:ring-blue-600"
              icon={<Award className="h-3.5 w-3.5" />}
            >
              See rewards
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

